import { ExternallinkIcon } from '@ordr-as/icons'
import { FailedPspNotificationDto, NonCapturedPaymentDto, UnknownStatePaymentDto } from '../../../generated-api'
import { FailedPspNotificationsTable } from './FailedNotifications/FailedPspNotificationsTable'
import { NonCapturedPaymentsTable } from './NonCapturedPayments/NonCapturedPaymentsTable'
import { UnknownStatePaymentsTable } from './UnknownStatePayments/UnknownStatePaymentsTable'
import CollapsiblePanel from './CollapsiblePanel'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Tab } from '@headlessui/react'
import config from '../../config'

interface PaymentAttentionListProps {
    failedPspNotifications: FailedPspNotificationDto[]
    nonCapturedPayments: NonCapturedPaymentDto[]
    unknownStatePayments: UnknownStatePaymentDto[]
}

const getDashboardUrl = () => {
    const isNotProd = config.env !== 'prod'
    const environment = isNotProd ? 'qa' : 'prod'
    const url = `https://dashboard.favrit.dev/d/NMcQcb07k/payment-service-v2?orgId=1&from=now-15m&to=now&timezone=browser&var-environment=${environment}&refresh=5s`
    return url
}

const PaymentAttentionList = ({
    failedPspNotifications,
    nonCapturedPayments,
    unknownStatePayments,
}: PaymentAttentionListProps) => {
    const { section } = useParams()

    const navigate = useNavigate()

    const tabKeys = ['psp-notifications', 'unknown-state', 'non-captured-payments'] as const
    const sectionToIndex = tabKeys.reduce((acc, key, idx) => ({ ...acc, [key]: idx }), {} as Record<string, number>)

    const selectedIndex = sectionToIndex[section ?? 'psp-notifications'] ?? 0

    // Optional: keep URL in sync when tab changes
    const handleTabChange = (index: number) => {
        navigate(`/payment-operations/${tabKeys[index]}`, { replace: true })
    }

    return (
        <div className="px-3">
            <CollapsiblePanel title="Payment Attention Overview" defaultOpen={false}>
                <div className="leading-relaxed">
                    <div className="space-y-2">
                        <p>Payments and notifications listed here need attention.</p>
                        <p>
                            Check out the{' '}
                            <a
                                href={getDashboardUrl()}
                                className="text-brand-primary inline-flex items-center gap-1"
                                target="_blank"
                                rel="noreferrer"
                            >
                                dashboard here
                                <ExternallinkIcon className="inline-block" />
                            </a>{' '}
                            (ask a friend for access)
                        </p>
                    </div>

                    <div className="mt-2">
                        <h4 className="font-semibold">Three types of issues:</h4>
                        <ul className="list-disc list-inside">
                            <li>
                                <Link to="/payment-operations/failed" className="text-brand-primary">
                                    Failed PSP notifications: <strong>{failedPspNotifications.length}</strong>
                                </Link>
                            </li>
                            <li>
                                <Link to="/payment-operations/unknown" className="text-brand-primary">
                                    UNKNOWN state payments: <strong>{unknownStatePayments.length}</strong>
                                </Link>
                            </li>
                            <li>
                                <Link to="/payment-operations/non-captured" className="text-brand-primary">
                                    Non-captured payments: <strong>{nonCapturedPayments.length}</strong>
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="space-y-2 mt-2">
                        <p>
                            Ideally, these should all be zero. Every payment or notification has real money behind it.
                        </p>
                        <p>
                            <strong>Better ask a friend if you&apos;re not sure.</strong>
                        </p>
                        <p>Good luck!</p>
                    </div>
                </div>
            </CollapsiblePanel>

            <Tab.Group selectedIndex={selectedIndex} onChange={handleTabChange}>
                <Tab.List className="mt-4 flex space-x-2">
                    {tabKeys.map((key) => (
                        <Tab
                            key={key}
                            className={({ selected }) =>
                                `w-1/3 tablet:w-1/4 desktop:w-1/5 py-4 leading-5 font-medium rounded-t-lg  shadow-lg z-10
                        ${
                            selected
                                ? 'bg-brand-white text-brand-black w-200 border-b-4 border-brand-primary'
                                : 'hover:bg-purple-lightest bg-grey-medium hover:text-brand-black text-grey-dark opacity-80 hover:opacity-100'
                        }`
                            }
                        >
                            {key === 'psp-notifications' &&
                                `Failed PSP notifications (${failedPspNotifications.length})`}
                            {key === 'unknown-state' && `Unknown state payments (${unknownStatePayments.length})`}
                            {key === 'non-captured-payments' && `Non-captured payments (${nonCapturedPayments.length})`}
                        </Tab>
                    ))}
                </Tab.List>

                <Tab.Panels className="relative z-10">
                    <Tab.Panel>
                        <FailedPspNotificationsTable failedPspNotifications={failedPspNotifications} />
                    </Tab.Panel>
                    <Tab.Panel>
                        <UnknownStatePaymentsTable unknownStatePayments={unknownStatePayments} />
                    </Tab.Panel>
                    <Tab.Panel>
                        <NonCapturedPaymentsTable nonCapturedPayments={nonCapturedPayments} />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </div>
    )
}
export default PaymentAttentionList
